import { useState, useEffect, useRef } from 'react'
import useCamera from '../hooks/useCamera'
import useFaceDetection from '../hooks/useFaceDetection'
import { createSession, updateSession, completeSession } from '../utils/api'
import DebugInfo from './DebugInfo'
import gifshot from 'gifshot'
import '../styles/design-system.css'
import '../styles/LivenessCheck.css'

const LivenessCheck = ({ onComplete }) => {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const detectionIntervalRef = useRef(null)
  const capturedFramesRef = useRef([]) // Store frames for GIF
  const lastFrameCaptureTime = useRef(0) // Track when last frame was captured

  const [sessionId, setSessionId] = useState(null)
  const [status, setStatus] = useState('initializing')
  const [instruction, setInstruction] = useState('Aguarde...')
  const [progress, setProgress] = useState(0)
  const [faceDetected, setFaceDetected] = useState(false)
  const [isFrontCamera, setIsFrontCamera] = useState(false)

  console.log('[LIVENESS] Component render, isFrontCamera:', isFrontCamera, 'status:', status)

  const { stream, error: cameraError, startCamera, stopCamera } = useCamera()
  const { detectFace, isModelLoaded } = useFaceDetection()

  // Initialize session and camera
  useEffect(() => {
    const initSession = async () => {
      try {
        const session = await createSession({ userAgent: navigator.userAgent })
        console.log('Session created:', session.sessionId)
        setSessionId(session.sessionId)
        await startCamera()
        setStatus('ready')
        setInstruction('Posicione seu rosto no centro da tela')
      } catch (err) {
        console.error('Failed to initialize session:', err)
        setInstruction('Erro ao inicializar. Recarregue a página.')
      }
    }

    initSession()

    return () => {
      stopCamera()
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current)
      }
    }
  }, [])

  // Setup video stream
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream
      console.log('Video stream attached')

      // Detect if using front camera
      const videoTrack = stream.getVideoTracks()[0]
      if (videoTrack) {
        const settings = videoTrack.getSettings()

        console.log('[CAMERA] ===== CAMERA DETECTION =====')
        console.log('[CAMERA] User Agent:', navigator.userAgent)
        console.log('[CAMERA] Video Track Settings:', settings)
        console.log('[CAMERA] facingMode:', settings.facingMode)
        console.log('[CAMERA] deviceId:', settings.deviceId)
        console.log('[CAMERA] label:', videoTrack.label)

        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        console.log('[CAMERA] Detected as Mobile:', isMobile)

        // Mirror logic: ALWAYS mirror front camera (user), NEVER mirror back camera (environment)
        // facingMode: 'user' = front camera (mirror), 'environment' = back camera (no mirror)
        // If undefined (like on Mac), assume front camera since it's for selfie/liveness
        const isFrontCamera = settings.facingMode === 'user' || settings.facingMode === undefined
        console.log('[CAMERA] Is Front Camera:', isFrontCamera, '(facingMode:', settings.facingMode, ')')

        // Mirror if front camera (regardless of mobile/desktop)
        const shouldMirror = isFrontCamera
        console.log('[CAMERA] Should Mirror:', shouldMirror)
        console.log('[CAMERA] ================================')

        setIsFrontCamera(shouldMirror)
      }
    }
  }, [stream])

  // Start detection when everything is ready
  useEffect(() => {
    if (status === 'ready' && isModelLoaded && stream && videoRef.current) {
      console.log('[READY] Starting detection loop in 1 second...')
      setInstruction('Posicione seu rosto no centro para começar')

      const timer = setTimeout(() => {
        setStatus('detecting')
      }, 1000)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [status, isModelLoaded, stream])

  // Separate effect for running detection loop
  useEffect(() => {
    if (status !== 'detecting') {
      return
    }

    console.log('[LOOP] Detection loop started')

    let detectionCount = 0
    let validDetectionCount = 0
    const maxDetections = 80 // Increased from 60
    let hasMovedClose = false
    let hasMovedFar = false
    let hasSeenFace = false
    let baselineSize = 0
    let maxSeenSize = 0
    let minSeenSize = Infinity
    let closeFrameCount = 0 // Count frames while close
    let farFrameCount = 0   // Count frames while far
    const detectedData = []

    const runDetection = async () => {
      if (!videoRef.current) {
        return
      }

      const width = videoRef.current.videoWidth
      const height = videoRef.current.videoHeight

      if (!width || !height) {
        // Log video dimensions on first few tries
        if (detectionCount < 5) {
          console.log(`[VIDEO] Waiting for video to load... width=${width}, height=${height}`)
        }
        return
      }

      // Log when video is ready (only once)
      if (detectionCount === 0) {
        console.log(`[VIDEO] Video ready! Dimensions: ${width}x${height}`)
      }

      try {
        const detection = await detectFace(videoRef.current)

        const size = detection ? Math.round(detection.size) : 0

        // Log every 30 detections (once per ~1.5 seconds)
        if (detectionCount % 30 === 0) {
          console.log(`[DETECTION ${detectionCount}] size=${size}, detected=${!!detection}`)
        }

        const dataPoint = {
          faceDetected: !!detection,
          faceSize: size,
          timestamp: Date.now(),
          // Add position data for natural movement validation
          centerX: detection ? detection.box.xCenter : null,
          centerY: detection ? detection.box.yCenter : null,
          boxWidth: detection ? detection.box.width : null,
          boxHeight: detection ? detection.box.height : null,
          // Add spoofing detection
          hasSuspiciousObject: detection ? detection.hasSuspiciousObject : false,
          suspiciousObjectType: detection ? detection.suspiciousObjectType : null
        }

        detectedData.push(dataPoint)
        detectionCount++

        // Only count valid detections (with face)
        if (detection) {
          setFaceDetected(true)

          // First time seeing face - establish baseline
          if (!hasSeenFace) {
            hasSeenFace = true
            baselineSize = size
            maxSeenSize = size
            minSeenSize = size
            console.log('✅ ROSTO DETECTADO! (BlazeFace) Baseline size:', Math.round(size))
            console.log(`   Thresholds: close=${Math.round(size * 1.15)}, return=${Math.round(size * 1.08)}`)
            setInstruction('Aproxime seu rosto da câmera')
            setProgress(0)
          }

          // Capture frame for GIF (1-2 FPS = 500-1000ms interval)
          const now = Date.now()
          if (hasSeenFace && now - lastFrameCaptureTime.current >= 500) {
            // Limit to 30 frames max
            if (capturedFramesRef.current.length < 30) {
              try {
                const canvas = document.createElement('canvas')
                // Resize to 320x240 for small GIF size
                canvas.width = 320
                canvas.height = 240
                const ctx = canvas.getContext('2d')
                ctx.drawImage(videoRef.current, 0, 0, 320, 240)

                // Convert to data URL (base64)
                const frameDataUrl = canvas.toDataURL('image/jpeg', 0.6)
                capturedFramesRef.current.push(frameDataUrl)
                lastFrameCaptureTime.current = now

                if (capturedFramesRef.current.length === 1) {
                  console.log('[GIF] Started capturing frames for GIF')
                }
              } catch (err) {
                console.error('[GIF] Error capturing frame:', err)
              }
            }
          }

          if (hasSeenFace) {
            // Track size range
            if (size > maxSeenSize) maxSeenSize = size
            if (size < minSeenSize) minSeenSize = size

            // Simplified: Only require CLOSE movement, then return to baseline
            const closeThreshold = baselineSize * 1.15 // 15% bigger (was working well)
            const returnThreshold = baselineSize * 1.08 // Back close to baseline (within 8%)

            // Debug info occasionally
            if (detectionCount % 20 === 0) {
              console.log(`[SIZE] current=${Math.round(size)}, baseline=${Math.round(baselineSize)}, need>${Math.round(closeThreshold)}, then<${Math.round(returnThreshold)}`)
            }

            // Phase 1: User needs to move CLOSE
            if (!hasMovedClose) {
              if (size > closeThreshold) {
                closeFrameCount++
                if (closeFrameCount >= 5) {
                  hasMovedClose = true
                  console.log(`✅ APROXIMAÇÃO confirmada! size=${Math.round(size)}`)
                  setInstruction('Perfeito! Agora volte à posição normal')
                }
              } else {
                closeFrameCount = 0
              }

              // Show progress toward close
              if (size > baselineSize) {
                const progressToClose = Math.min(1, (size - baselineSize) / (closeThreshold - baselineSize))
                setProgress(progressToClose * 45)
              }

              // Show final progress while maintaining close position
              if (closeFrameCount > 0) {
                setProgress(45 + (closeFrameCount / 5) * 5)
              }
            }
            // Phase 2: User needs to return to near baseline (not far, just normal)
            else if (!hasMovedFar) {
              if (size < returnThreshold) {
                farFrameCount++
                if (farFrameCount >= 5) {
                  hasMovedFar = true
                  console.log(`✅ RETORNO confirmado! size=${Math.round(size)}`)
                  setInstruction('Excelente! Finalizando...')
                }
              } else {
                farFrameCount = 0
              }

              // Show progress toward return
              if (size < closeThreshold) {
                const progressToReturn = Math.max(0, Math.min(1, (closeThreshold - size) / (closeThreshold - returnThreshold)))
                setProgress(50 + progressToReturn * 45)
              } else {
                setProgress(50)
              }

              // Show final progress while maintaining return position
              if (farFrameCount > 0) {
                setProgress(95 + (farFrameCount / 5) * 5)
              }
            }
          }

          validDetectionCount++
        } else {
          setFaceDetected(false)

          // If we haven't seen a face yet, remind user
          if (!hasSeenFace) {
            setInstruction('Posicione seu rosto no centro para começar')
          }
        }

        // Send to backend periodically (for real-time monitoring if needed)
        if (detectionCount % 20 === 0 && sessionId) {
          await updateSession(sessionId, dataPoint)
        }

        // Complete detection when BOTH movements are confirmed
        if (hasMovedClose && hasMovedFar) {
          console.log('✅ Verificação completa! Processando...')
          setProgress(100)
          clearInterval(detectionIntervalRef.current)
          finishDetection(detectedData)
        }

        // Timeout after too many frames
        if (detectionCount >= 300) { // 15 seconds timeout
          console.log('❌ Timeout - tempo esgotado')
          clearInterval(detectionIntervalRef.current)
          setInstruction('Tempo esgotado. Recarregue para tentar novamente.')
        }
      } catch (err) {
        console.error('[DETECTION] Error:', err)
      }
    }

    // Start interval
    detectionIntervalRef.current = setInterval(runDetection, 50)

    // Cleanup
    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current)
        detectionIntervalRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  const finishDetection = async (detectedData) => {
    setStatus('processing')
    setInstruction('Processando verificação...')

    console.log(`[FINISH] Sending ${detectedData.length} data points to backend`)

    try {
      // Send all accumulated data points to backend
      const result = await completeSession(sessionId, {
        totalDetections: detectedData.length,
        faceDetections: detectedData.filter(d => d.faceDetected).length,
        allData: detectedData // Send all accumulated detection data
      })

      console.log('[FINISH] Verification result:', result)

      if (result.verificationResult.isLive) {
        setStatus('complete')
        setInstruction('✅ Captura concluída!')

        // Capture selfie from video before stopping camera
        let selfieBlob = null
        if (videoRef.current) {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = videoRef.current.videoWidth
            canvas.height = videoRef.current.videoHeight
            const ctx = canvas.getContext('2d')
            ctx.drawImage(videoRef.current, 0, 0)

            selfieBlob = await new Promise((resolve) => {
              canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95)
            })
            console.log('[FINISH] Selfie captured, size:', selfieBlob?.size)
          } catch (err) {
            console.error('[FINISH] Failed to capture selfie:', err)
          }
        }

        // Generate GIF from captured frames
        let gifBlob = null
        if (capturedFramesRef.current.length > 0) {
          try {
            console.log(`[GIF] Generating GIF from ${capturedFramesRef.current.length} frames...`)

            gifBlob = await new Promise((resolve, reject) => {
              gifshot.createGIF({
                images: capturedFramesRef.current,
                gifWidth: 320,
                gifHeight: 240,
                interval: 0.125, // 125ms between frames (8 FPS) - 4x faster
                numFrames: capturedFramesRef.current.length,
                frameDuration: 1.25, // 1.25 * 100ms = 125ms per frame
                sampleInterval: 10, // Lower quality for smaller file
                numWorkers: 2
              }, (obj) => {
                if (obj.error) {
                  console.error('[GIF] Error generating GIF:', obj.error)
                  reject(obj.error)
                } else {
                  // Convert base64 to blob
                  fetch(obj.image)
                    .then(res => res.blob())
                    .then(blob => {
                      console.log('[GIF] GIF generated successfully, size:', blob.size, 'bytes')
                      resolve(blob)
                    })
                    .catch(reject)
                }
              })
            })
          } catch (err) {
            console.error('[GIF] Failed to generate GIF:', err)
            // Don't block the flow if GIF generation fails
            gifBlob = null
          }
        } else {
          console.log('[GIF] No frames captured, skipping GIF generation')
        }

        stopCamera()

        if (onComplete) {
          onComplete({
            ...result.verificationResult,
            selfieBlob, // Include the selfie blob
            gifBlob // Include the GIF blob (may be null)
          })
        }
      } else {
        // Failed validation - show detailed reason
        setStatus('failed')
        const metrics = result.verificationResult.metrics
        const reason = result.verificationResult.reason || 'Unknown'

        console.error('[VALIDATION FAILED]', {
          reason,
          metrics
        })

        setInstruction(`❌ Verificação falhou: ${reason}`)

        // Auto-reload after 3 seconds
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      }
    } catch (err) {
      console.error('[FINISH] Error:', err)
      setInstruction('Erro ao processar. Tente novamente.')
    }
  }

  if (cameraError) {
    return (
      <div className="document-capture-container">
        <div className="document-capture-card">
          <div className="document-capture-error">
            <p>❌ Erro ao acessar câmera: {cameraError}</p>
            <p>Permita o acesso à câmera e recarregue a página.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="document-capture-container">
      <div className="document-capture-card">
        <div className="document-header">
          <div className="document-header-badge">
            <div className="badge-dot"></div>
            VERIFICAÇÃO DE IDENTIDADE
          </div>
          <h2>Confirme sua identidade</h2>
          <p className="document-subtitle">Processo seguro com criptografia de ponta a ponta</p>
        </div>

        {/* Barra de progresso */}
        <div className="progress-steps">
          <div className="progress-step active">
            <div className="step-circle">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
              </svg>
            </div>
            <div className="step-label">DADOS</div>
          </div>

          <div className="progress-line active"></div>

          <div className="progress-step active">
            <div className="step-circle">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
              </svg>
            </div>
            <div className="step-label">DOCUMENTO</div>
          </div>

          <div className="progress-line active"></div>

          <div className="progress-step current">
            <div className="step-circle">3</div>
            <div className="step-label">SELFIE</div>
          </div>

          <div className="progress-line"></div>

          <div className="progress-step">
            <div className="step-circle">4</div>
            <div className="step-label">PRONTO</div>
          </div>
        </div>

        <div className="video-container">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`video-feed ${isFrontCamera ? 'mirror' : ''}`}
            onLoadedMetadata={() => console.log('[LIVENESS] Video loaded, className:', `video-feed ${isFrontCamera ? 'mirror' : ''}`, 'isFrontCamera:', isFrontCamera)}
          />
          <canvas ref={canvasRef} className={`detection-canvas ${isFrontCamera ? 'mirror' : ''}`} style={{ display: 'none' }} />

          <div className="liveness-overlay">
            <div className={`face-oval ${faceDetected ? 'face-detected' : ''}`} />

            {/* Instrução dentro do vídeo */}
            <div className="liveness-instruction-overlay">{instruction}</div>

            {/* Barra de progresso dentro do vídeo */}
            {status === 'detecting' && (
              <div className="liveness-progress-overlay">
                <div
                  className="liveness-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="controls">
          {/* Controles vazios - instrução agora está dentro do vídeo */}
        </div>

        <div className="document-footer">
          <div className="footer-security">
            <svg className="footer-security-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Seus dados são protegidos e criptografados
          </div>
        </div>
      </div>
    </div>
  )
}

export default LivenessCheck
